import { graphqlHostFromEndpoint, normalizeRealtimeWsUrl, realtimeEndpointFromGraphQL, } from "./httpClient.js";
import { SURF_APP_ID } from "../constants.js";
const CONNECTION_ACK_TIMEOUT_MS = 10_000;
const PROACTIVE_TOKEN_REFRESH_MS = 50 * 60 * 1000;
/** AppSync sends `ka` ~60s; force reconnect if none received (silent dead socket). */
const KEEPALIVE_TIMEOUT_MS = 90_000;
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
function backoffDelayMs(attempt) {
    const index = Math.min(Math.max(attempt, 0), BACKOFF_MS.length - 1);
    const base = BACKOFF_MS[index] ?? 30_000;
    const jitter = 0.75 + Math.random() * 0.5;
    return Math.round(base * jitter);
}
/** Standard Base64 for AppSync query-string handshake (aligned with AWS docs). */
export function base64EncodeUtf8(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (const b of bytes)
        binary += String.fromCharCode(b);
    return btoa(binary);
}
/** Builds AppSync realtime WebSocket URL (query params URL-encoded standard Base64). */
export function buildAppSyncRealtimeWsUrl(realtimeEndpoint, graphqlEndpoint, idToken) {
    const host = graphqlHostFromEndpoint(graphqlEndpoint);
    const header = base64EncodeUtf8(JSON.stringify({
        Authorization: idToken,
        host,
    }));
    const payload = base64EncodeUtf8("{}");
    const base = normalizeRealtimeWsUrl(realtimeEndpoint);
    return `${base}?header=${encodeURIComponent(header)}&payload=${encodeURIComponent(payload)}`;
}
function isUnauthorizedError(error) {
    const errorType = error.errorType ?? "";
    const message = (error.message ?? "").toLowerCase();
    return (errorType.includes("Unauthorized") ||
        message.includes("unauthorized") ||
        message.includes("token has expired"));
}
let subCounter = 0;
function detachWebSocket(ws) {
    ws.onopen = null;
    ws.onerror = null;
    ws.onclose = null;
    ws.onmessage = null;
}
/**
 * Minimal AppSync realtime (graphql-ws) client for Surf's ephemeral presenter channel.
 * Ported from the shared Vida realtime client pattern (compass/wave/tide).
 */
export class SurfRealtimeClient {
    ws = null;
    socketGeneration = 0;
    connectInFlight = null;
    subs = new Map();
    reconnectTimer = null;
    ackTimeoutTimer = null;
    tokenRefreshTimer = null;
    subRestartTimers = new Map();
    reconnectAttempt = 0;
    subRestartAttempts = new Map();
    connectionStableTimer = null;
    keepaliveTimer = null;
    lastKaAt = 0;
    networkListenersBound = false;
    onOnline = null;
    onVisibilityChange = null;
    closed = false;
    connected = false;
    startedSubIds = new Set();
    graphqlEndpoint;
    realtimeEndpoint;
    getIdToken;
    onConnectionChange;
    onStatusChange;
    onError;
    constructor(opts) {
        this.graphqlEndpoint = opts.graphqlEndpoint;
        this.realtimeEndpoint = normalizeRealtimeWsUrl(opts.realtimeEndpoint?.trim() || realtimeEndpointFromGraphQL(opts.graphqlEndpoint));
        this.getIdToken = opts.getIdToken;
        this.onConnectionChange = opts.onConnectionChange;
        this.onStatusChange = opts.onStatusChange;
        this.onError = opts.onError;
    }
    async connect() {
        this.closed = false;
        this.bindNetworkListeners();
        if (this.connectInFlight) {
            await this.connectInFlight;
            return;
        }
        this.connectInFlight = this.openSocket()
            .catch(() => {
            this.publishStatus();
            if (!this.closed)
                this.scheduleReconnect();
        })
            .finally(() => {
            this.connectInFlight = null;
        });
        await this.connectInFlight;
    }
    disconnect() {
        this.closed = true;
        this.socketGeneration += 1;
        this.connectInFlight = null;
        this.unbindNetworkListeners();
        this.clearTimers();
        const ws = this.ws;
        this.ws = null;
        if (ws) {
            detachWebSocket(ws);
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                try {
                    ws.close();
                }
                catch {
                    /* ignore */
                }
            }
        }
        this.connected = false;
        this.startedSubIds.clear();
        this.onConnectionChange?.(false);
        this.publishStatus();
    }
    /**
     * On mobile (esp. iOS Safari) the WS is killed in background/lock. Instead of
     * waiting for the exponential backoff (up to ~30s), reconnect eagerly as soon
     * as the network is back or the tab becomes visible again.
     */
    bindNetworkListeners() {
        if (this.networkListenersBound)
            return;
        if (typeof window === "undefined")
            return;
        this.onOnline = () => this.handleNetworkWake();
        this.onVisibilityChange = () => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                this.handleNetworkWake();
            }
        };
        window.addEventListener("online", this.onOnline);
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", this.onVisibilityChange);
        }
        this.networkListenersBound = true;
    }
    unbindNetworkListeners() {
        if (!this.networkListenersBound)
            return;
        if (typeof window !== "undefined" && this.onOnline) {
            window.removeEventListener("online", this.onOnline);
        }
        if (typeof document !== "undefined" && this.onVisibilityChange) {
            document.removeEventListener("visibilitychange", this.onVisibilityChange);
        }
        this.onOnline = null;
        this.onVisibilityChange = null;
        this.networkListenersBound = false;
    }
    handleNetworkWake() {
        if (this.closed || this.subs.size === 0)
            return;
        if (!this.connected) {
            // Cancel pending backoff and retry immediately.
            this.reconnectAttempt = 0;
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
            void this.openSocket().catch(() => {
                if (!this.closed)
                    this.scheduleReconnect();
            });
            return;
        }
        // Connected but possibly a silent dead socket: if keepalive lapsed, drop it
        // so the onclose handler restarts the reconnect cycle immediately.
        if (Date.now() - this.lastKaAt > KEEPALIVE_TIMEOUT_MS) {
            this.ws?.close();
        }
    }
    isConnected() {
        return this.connected;
    }
    getStatus() {
        if (this.connected)
            return "online";
        if (this.subs.size > 0)
            return "connecting";
        return "offline";
    }
    subscribe(query, variables, handler) {
        const id = `sub-${++subCounter}`;
        this.subs.set(id, {
            id,
            query,
            variables,
            handler: handler,
        });
        if (this.connected) {
            void this.startSubscription(id);
        }
        else {
            void this.connect().catch(() => {
                /* connect handles reconnect */
            });
        }
        return () => {
            const restart = this.subRestartTimers.get(id);
            if (restart) {
                clearTimeout(restart);
                this.subRestartTimers.delete(id);
            }
            this.subs.delete(id);
            this.startedSubIds.delete(id);
            this.send({ type: "stop", id });
            this.publishStatus();
        };
    }
    publishStatus() {
        this.onStatusChange?.(this.getStatus());
    }
    clearTimers() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ackTimeoutTimer) {
            clearTimeout(this.ackTimeoutTimer);
            this.ackTimeoutTimer = null;
        }
        if (this.tokenRefreshTimer) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
        if (this.connectionStableTimer) {
            clearTimeout(this.connectionStableTimer);
            this.connectionStableTimer = null;
        }
        this.stopKeepaliveWatchdog();
        for (const timer of this.subRestartTimers.values()) {
            clearTimeout(timer);
        }
        this.subRestartTimers.clear();
    }
    async openSocket() {
        if (this.closed)
            return;
        if (this.connected && this.ws?.readyState === WebSocket.OPEN) {
            return;
        }
        const existing = this.ws;
        if (existing?.readyState === WebSocket.CONNECTING) {
            return;
        }
        if (existing) {
            this.socketGeneration += 1;
            detachWebSocket(existing);
            try {
                existing.close();
            }
            catch {
                /* ignore */
            }
            this.ws = null;
        }
        const generation = this.socketGeneration + 1;
        this.socketGeneration = generation;
        this.connected = false;
        this.startedSubIds.clear();
        this.publishStatus();
        let token;
        try {
            token = await this.getIdToken();
        }
        catch {
            return;
        }
        if (this.closed || generation !== this.socketGeneration)
            return;
        const url = buildAppSyncRealtimeWsUrl(this.realtimeEndpoint, this.graphqlEndpoint, token);
        return new Promise((resolve) => {
            try {
                this.ws = new WebSocket(url, "graphql-ws");
            }
            catch {
                resolve();
                return;
            }
            const ws = this.ws;
            let settled = false;
            const settleFailed = () => {
                if (settled)
                    return;
                settled = true;
                if (this.ackTimeoutTimer) {
                    clearTimeout(this.ackTimeoutTimer);
                    this.ackTimeoutTimer = null;
                }
                resolve();
            };
            this.ackTimeoutTimer = setTimeout(() => {
                if (settled || this.connected)
                    return;
                settled = true;
                ws.close();
                resolve();
            }, CONNECTION_ACK_TIMEOUT_MS);
            ws.onopen = () => {
                this.send({ type: "connection_init" });
            };
            ws.onmessage = (ev) => {
                if (generation !== this.socketGeneration)
                    return;
                const msg = JSON.parse(String(ev.data));
                this.handleMessage(msg);
                if (msg.type === "connection_ack" && !settled) {
                    settled = true;
                    if (this.ackTimeoutTimer) {
                        clearTimeout(this.ackTimeoutTimer);
                        this.ackTimeoutTimer = null;
                    }
                    this.connected = true;
                    this.reconnectAttempt = 0;
                    this.scheduleConnectionStableReset();
                    this.startKeepaliveWatchdog();
                    this.onConnectionChange?.(true);
                    this.publishStatus();
                    this.startTokenRefreshLoop();
                    this.startedSubIds.clear();
                    for (const sub of this.subs.values()) {
                        this.subRestartAttempts.set(sub.id, 0);
                        void this.startSubscription(sub.id);
                    }
                    resolve();
                }
            };
            ws.onerror = () => {
                if (generation !== this.socketGeneration)
                    return;
                settleFailed();
            };
            ws.onclose = () => {
                if (generation !== this.socketGeneration)
                    return;
                this.connected = false;
                this.startedSubIds.clear();
                this.onConnectionChange?.(false);
                this.stopTokenRefreshLoop();
                this.stopKeepaliveWatchdog();
                this.publishStatus();
                if (!settled)
                    settleFailed();
                if (!this.closed) {
                    this.scheduleReconnect();
                }
            };
        });
    }
    scheduleReconnect() {
        if (this.reconnectTimer || this.closed)
            return;
        const attempt = this.reconnectAttempt;
        this.reconnectAttempt += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            void this.openSocket().catch(() => this.scheduleReconnect());
        }, backoffDelayMs(attempt));
    }
    scheduleConnectionStableReset() {
        if (this.connectionStableTimer) {
            clearTimeout(this.connectionStableTimer);
        }
        this.connectionStableTimer = setTimeout(() => {
            this.connectionStableTimer = null;
            this.reconnectAttempt = 0;
            this.subRestartAttempts.clear();
        }, 60_000);
    }
    startTokenRefreshLoop() {
        this.stopTokenRefreshLoop();
        this.tokenRefreshTimer = setInterval(() => {
            void this.getIdToken(true).catch(() => {
                /* best effort */
            });
        }, PROACTIVE_TOKEN_REFRESH_MS);
    }
    stopTokenRefreshLoop() {
        if (this.tokenRefreshTimer) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
    }
    startKeepaliveWatchdog() {
        this.stopKeepaliveWatchdog();
        this.lastKaAt = Date.now();
        const tick = () => {
            if (!this.connected || this.closed)
                return;
            if (Date.now() - this.lastKaAt > KEEPALIVE_TIMEOUT_MS) {
                this.ws?.close();
                return;
            }
            this.keepaliveTimer = setTimeout(tick, KEEPALIVE_TIMEOUT_MS / 3);
        };
        this.keepaliveTimer = setTimeout(tick, KEEPALIVE_TIMEOUT_MS / 3);
    }
    stopKeepaliveWatchdog() {
        if (this.keepaliveTimer) {
            clearTimeout(this.keepaliveTimer);
            this.keepaliveTimer = null;
        }
    }
    touchKeepalive() {
        this.lastKaAt = Date.now();
    }
    send(msg) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(msg));
        }
    }
    parseSubscriptionPayload(payload) {
        const dataField = payload?.data;
        if (typeof dataField === "string") {
            const parsed = JSON.parse(dataField);
            const keys = Object.keys(parsed);
            return keys[0] ? parsed[keys[0]] : undefined;
        }
        if (dataField && typeof dataField === "object") {
            const values = Object.values(dataField);
            return values[0];
        }
        return undefined;
    }
    async startSubscription(subId) {
        const sub = this.subs.get(subId);
        if (!sub || !this.connected)
            return;
        if (this.startedSubIds.has(subId))
            return;
        const token = await this.getIdToken();
        const host = graphqlHostFromEndpoint(this.graphqlEndpoint);
        this.send({
            id: sub.id,
            type: "start",
            payload: {
                data: JSON.stringify({
                    query: sub.query,
                    variables: sub.variables,
                }),
                extensions: {
                    authorization: {
                        Authorization: token,
                        host,
                        "X-Vida-AppId": SURF_APP_ID,
                    },
                },
            },
        });
        this.startedSubIds.add(subId);
    }
    scheduleSubscriptionRestart(subId) {
        const existing = this.subRestartTimers.get(subId);
        if (existing)
            clearTimeout(existing);
        const attempt = (this.subRestartAttempts.get(subId) ?? 0) + 1;
        this.subRestartAttempts.set(subId, attempt);
        this.subRestartTimers.set(subId, setTimeout(() => {
            this.subRestartTimers.delete(subId);
            if (this.subs.has(subId) && this.connected) {
                void this.startSubscription(subId);
            }
        }, backoffDelayMs(attempt - 1)));
    }
    async handleUnauthorizedReconnect() {
        await this.getIdToken(true).catch(() => {
            /* best effort */
        });
        this.ws?.close();
    }
    handleMessage(msg) {
        switch (msg.type) {
            case "ka":
                this.touchKeepalive();
                return;
            case "connection_error": {
                this.ws?.close();
                return;
            }
            case "start_ack":
                return;
            case "data": {
                this.touchKeepalive();
                if (!msg.id || !msg.payload)
                    return;
                const sub = this.subs.get(msg.id);
                if (!sub)
                    return;
                try {
                    const parsed = this.parseSubscriptionPayload(msg.payload);
                    if (parsed !== undefined) {
                        sub.handler(parsed);
                    }
                }
                catch {
                    /* ignore malformed */
                }
                return;
            }
            case "error": {
                const errors = msg.payload?.errors ?? [];
                if (errors.some(isUnauthorizedError)) {
                    void this.handleUnauthorizedReconnect();
                    return;
                }
                // Surface subscription-level errors (bad filter, schema mismatch, appId gate)
                // so callers can show a diagnostic instead of a silent "waiting" state.
                this.onError?.(errors.length > 0 ? errors : msg);
                if (msg.id && this.subs.has(msg.id)) {
                    this.startedSubIds.delete(msg.id);
                    this.send({ type: "stop", id: msg.id });
                    this.scheduleSubscriptionRestart(msg.id);
                }
                return;
            }
            default:
                return;
        }
    }
}
